// Renders the per-project chat threads as the design's mode-coloured
// `thread-card` stack. Click a card → drawer opens with that session.

import type { JSX } from "react";

import type { ProjectThread } from "@loom/shared/dashboard";

import { useRelativeTime } from "../../i18n/format.js";
import { useT } from "../../i18n/index.js";

interface ThreadsBodyProps {
  readonly threads: ReadonlyArray<ProjectThread>;
  readonly loading: boolean;
  readonly error: string | null;
  readonly onOpenThread: (sessionId: string) => void;
}

export const ThreadsBody = ({
  threads,
  loading,
  error,
  onOpenThread,
}: ThreadsBodyProps): JSX.Element => {
  const tr = useT();
  const rel = useRelativeTime();
  if (loading) {
    return <div className="threads-body"><p className="muted">{tr("dashboard.threads.loading")}</p></div>;
  }
  if (error !== null) {
    return (
      <div className="threads-body">
        <p className="form-error">{error}</p>
      </div>
    );
  }
  if (threads.length === 0) {
    return (
      <div className="threads-body">
        <p className="muted">{tr("dashboard.threads.empty")}</p>
      </div>
    );
  }
  return (
    <div className="threads-body">
      {threads.map((t) => (
        <div
          key={t.sessionId}
          className={`thread-card mode-${t.mode}`}
          onClick={() => onOpenThread(t.sessionId)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onOpenThread(t.sessionId);
            }
          }}
        >
          <div className="thread-card-head">
            <span className={`thread-mode-tag ${t.mode}`}>{t.mode}</span>
            <span className="who">{t.live ? tr("dashboard.threads.live") : tr("dashboard.threads.session")}</span>
            {/* From the ISO instant, not the server's rendered "4m ago" —
                that string is English in a German interface. */}
            <span className="when">{rel(t.lastActiveAt)}</span>
          </div>
          <div className="thread-card-title">{t.title}</div>
          {t.preview.length > 0 && (
            <div className="thread-card-preview">{t.preview}</div>
          )}
        </div>
      ))}
    </div>
  );
};
